import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Handshake } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { Frame } from '../components/shell/AppShell';
import { Panel, PanelHeading } from '../components/ui/panel';
import swornArt from '../assets/bg/the-sworn.webp';
import { swornParent } from '../lib/navigation';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Being asked into the Sworn.
 *
 * docs/sworn_design.md §7: membership is offered and accepted, not assigned.
 *
 * The four sentences below are not marketing and must not be softened. They
 * are the things somebody should know BEFORE rather than after, and the last
 * one is the one that matters: leaving the circle stops the software showing
 * your name to the people in it. It does not make them forget it. A screen
 * that implies otherwise would be selling a protection nobody can deliver.
 *
 * §7.1: REFUSING MUST BE ORDINARY. No confirmation dialogue, no "are you
 * sure?", no warning about what they are turning down, and nothing sent back
 * to the Founder that says refused.
 *
 * 2.0 (approved as a consent-design change): the two answers are now IDENTICAL
 * in weight -- same size, same outline, same colour. Before, Accept was filled
 * gold and No, thank you an outline, which quietly told the eye which one was
 * expected. Nothing on this screen may lean towards yes.
 */
const SwornInvitation = () => {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/api/sworn/invitation`, { withCredentials: true })
      .then(() => setLoading(false))
      // A 404 means there is no offer waiting -- never asked, already
      // answered, or lapsed. None of them is a state to explain, and a screen
      // that said "your invitation expired" would tell somebody who was never
      // invited that there is something here to be invited to.
      .catch(() => navigate('/', { replace: true }));
  }, [navigate]);

  const accept = async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await axios.post(`${API_URL}/api/sworn/invitation/accept`, {},
        { withCredentials: true });
      // The flag lives on the user document and reaches the app through
      // /auth/me, so the session has to be re-read before the room exists for
      // them. Without this they would accept and land on a page that 404s.
      await checkAuth();
      navigate('/sworn', { replace: true });
    } catch (e) {
      setError('That did not go through. Try again.');
      setBusy(false);
    }
  };

  const decline = async () => {
    if (busy) return;
    setBusy(true);
    // No confirmation, and no waiting to see what the server says -- there is
    // nothing it could tell us that the person needs to hear. They said no;
    // the screen closes.
    axios.post(`${API_URL}/api/sworn/invitation/decline`, {},
      { withCredentials: true }).catch(() => { /* nothing to report */ });
    navigate('/', { replace: true });
  };

  // One style for both answers. Change it here and both change together.
  const answer = 'flex-1 h-12 border border-primary/60 bg-card/60 text-foreground hover:bg-primary/10 font-medium';

  return (
    <Frame user={user} background={swornArt} scrim={0.7} activeKey={swornParent(user).key}>
      <div
        className="flex flex-col items-center justify-center px-4 py-8"
        style={{ minHeight: 'calc(100dvh - 64px - env(safe-area-inset-bottom) - env(safe-area-inset-top))' }}
        data-testid="sworn-invitation-page"
      >
        {loading ? (
          <p className="text-sm text-muted-foreground" role="status">One moment…</p>
        ) : (
          <Panel className="w-full max-w-md p-6">
            <div className="text-center mb-6">
              <Handshake className="w-8 h-8 mx-auto mb-3" style={{ color: 'hsl(var(--gold))' }} weight="fill" aria-hidden="true" />
              <PanelHeading as="h1" className="text-2xl">The Sworn</PanelHeading>
              <p className="text-sm text-muted-foreground mt-2">You have been asked to join.</p>
              <div className="mx-auto mt-4 h-px w-2/3" style={{ background: 'hsl(var(--gold) / 0.3)' }} aria-hidden="true" />
            </div>

            {/* The four sentences from §7, in the order they are written there and
                with no fifth one added. Anything else on this screen would be
                persuasion, and persuasion is the thing §7.1 exists to keep out. */}
            <ul className="space-y-3 mb-8 text-sm leading-relaxed text-foreground" data-testid="invitation-disclosures">
              <li className="flex gap-3">
                <span style={{ color: 'hsl(var(--gold))' }} aria-hidden="true">·</span>
                <span>Everyone in this circle will see your name.</span>
              </li>
              <li className="flex gap-3">
                <span style={{ color: 'hsl(var(--gold))' }} aria-hidden="true">·</span>
                <span>You will see theirs.</span>
              </li>
              <li className="flex gap-3">
                <span style={{ color: 'hsl(var(--gold))' }} aria-hidden="true">·</span>
                <span>The Founder is in the room, as a member of it.</span>
              </li>
              <li className="flex gap-3">
                <span style={{ color: 'hsl(var(--gold))' }} aria-hidden="true">·</span>
                <span>
                  Leaving stops the software showing your name. It does not unsee it.
                </span>
              </li>
            </ul>

            {error && (
              <p role="alert" className="text-sm text-destructive mb-3" data-testid="invitation-error">
                {error}
              </p>
            )}

            {/* Same size, same row, same weight: neither one quieter. */}
            <div className="flex gap-3">
              <Button
                variant="ghost"
                className={answer}
                onClick={decline}
                disabled={busy}
                data-testid="invitation-decline"
              >
                No, thank you
              </Button>
              <Button
                variant="ghost"
                className={answer}
                onClick={accept}
                disabled={busy}
                data-testid="invitation-accept"
              >
                Accept
              </Button>
            </div>

            {/* The Founder-approved wording. The old line said nobody is told
                "either way", which was not true: accepting is recorded and
                makes the name visible to the circle. Declining tells nobody. */}
            <p className="text-xs text-muted-foreground text-center mt-6" data-testid="invitation-footnote">
              You can answer later. Nobody is told if you say no.
            </p>
          </Panel>
        )}
      </div>
    </Frame>
  );
};

export default SwornInvitation;
